import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import {
    AbstractUploadSandboxService,
    ApiRecordType,
    FragmentId,
    GenerateGuid,
    IUploadItem,
    MediaHavenPlugin,
    MediaObjectId,
    PluginGuard
} from '@stylehaven/components';
import { DirectUploadSandboxService } from './direct-upload.sandbox.service';
import { ResumableUploadSandboxService } from './resumable-upload.sandbox.service';

@Injectable({ providedIn: 'root' })
export class UploadSandboxService {
    private readonly resumableUploadSandbox: ResumableUploadSandboxService = inject(ResumableUploadSandboxService);
    private readonly simpleUploadSandbox: DirectUploadSandboxService = inject(DirectUploadSandboxService);
    private readonly pluginGuard: PluginGuard = inject(PluginGuard);

    // Simple Upload by default
    private uploadSandbox: AbstractUploadSandboxService = this.simpleUploadSandbox;

    constructor() {
        this.initUploadSandbox();
    }

    private initUploadSandbox(): void {
        this.pluginGuard
            .checkPlugins([MediaHavenPlugin.RESUMABLE_UPLOADS_MH])
            .pipe(take(1))
            .subscribe(pluginExists => {
                if (pluginExists) {
                    this.uploadSandbox = this.resumableUploadSandbox;
                }
                this.uploadSandbox.init();
            });
    }

    public uploadFile(
        file: File,
        zoneId: string,
        parentRecordId: MediaObjectId,
        recordType: ApiRecordType,
        group: string = GenerateGuid()
    ): void {
        return this.uploadSandbox.uploadFile(file, zoneId, parentRecordId, recordType, group);
    }

    public uploadFiles(
        files: FileList,
        zoneId: string,
        parentRecordId: MediaObjectId,
        recordType: ApiRecordType,
        group: string = GenerateGuid()
    ): Observable<boolean> {
        return this.uploadSandbox.uploadFiles(files, zoneId, parentRecordId, recordType, group);
    }

    public uploadNewOriginal(file: File, fragmentId: FragmentId): Observable<boolean> {
        return this.uploadSandbox.uploadNewOriginal(file, fragmentId);
    }

    public cancelOrAbortUpload(uploadItem: IUploadItem, abort?: boolean): void {
        return this.uploadSandbox.cancelOrAbortUpload(uploadItem, abort);
    }

    public cancelUpload(syncData: { tempId: string; uploadItemUpdate: Partial<IUploadItem> }): void {
        return this.uploadSandbox.cancelUpload(syncData);
    }

    public canPauseUpload(uploadItem: IUploadItem): boolean {
        return this.uploadSandbox.isPausable(uploadItem);
    }

    public pauseUpload(uploadItem: IUploadItem): void {
        return this.uploadSandbox.pauseUpload(uploadItem);
    }

    public canResumeUpload(uploadItem: IUploadItem): boolean {
        return this.uploadSandbox.isResumable(uploadItem);
    }

    public resumeUpload(uploadItem: IUploadItem): void {
        return this.uploadSandbox.resumeUpload(uploadItem);
    }

    public onBrowserRefresh(): Observable<void> {
        return this.uploadSandbox.onBrowserRefresh();
    }
}
